import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Alert, Box, Button, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import { useFamilyChild } from './FamilyShell';
import { parentApi } from './parentApi';
import { formatWhen } from './familyFormat';

export default function FamilyLeave() {
  const { child } = useFamilyChild();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const preset = params.get('sessionId') || '';
  const q = useQuery(
    ['familyUpcoming', child.studentId],
    async () => (await parentApi.upcomingSessions(child.studentId)).data?.data || [],
    { enabled: !!child.studentId }
  );
  const sessions = q.data || [];
  const [picked, setPicked] = useState(() => (preset ? { [preset]: true } : {}));
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const selected = sessions.filter((s) => picked[s.sessionId]);
  const copy = useMemo(() => selected[0]?.makeupCopy || '', [selected]);

  const toggle = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }));

  const send = async () => {
    setErr('');
    const sessionIds = Object.keys(picked).filter((id) => picked[id]);
    if (!sessionIds.length) {
      setErr('Pick at least one class.');
      return;
    }
    setSaving(true);
    try {
      await parentApi.applyLeave(child.studentId, { sessionIds, reason });
      navigate('/family');
    } catch (e) {
      setErr(e.response?.data?.message || 'Could not send.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6">Can't attend</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Which class will {child.firstName} miss?
      </Typography>
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {sessions.map((s) => (
        <FormControlLabel
          key={s.sessionId}
          control={<Checkbox checked={!!picked[s.sessionId]} onChange={() => toggle(s.sessionId)} disabled={s.alreadyLeave} />}
          label={
            <Box>
              <Typography>{formatWhen(s)}{s.alreadyLeave ? ' · already told' : ''}</Typography>
              <Typography variant="caption" color="text.secondary">{s.batchName} · {s.teacherName}</Typography>
            </Box>
          }
          sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}
        />
      ))}
      {copy && <Alert severity={selected[0]?.makeupEligible ? 'success' : 'info'} sx={{ my: 2 }}>{copy}</Alert>}
      <TextField fullWidth label="Reason, if you'd like" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mb: 2 }} />
      <Button variant="contained" onClick={send} disabled={saving}>Send request</Button>
    </Box>
  );
}
